import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase/config';
import { Building2, Stethoscope, Microscope, Factory, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import './Register.css';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import AddressPicker from './common/AddressPicker';
import { getFeatures, isTermsV70Effective, isTermsV80Effective } from '../utils/features';
// 🏭 S5 가입 위주 외주 업체 등록(2026-10-27, 계획 docs/계획_가입위주_외주업체등록_S5_2026-10-27.md §2-B): 번호는 users 가 아니라 vendorDocs 메타에, 서약 체크 1개, 기공소 「기공소 확인」 블록.
//   날짜 게이트: 처리방침 v3.3.5 시행(2026-11-03 00:00 KST, config/privacyV335.js) 전에는 번호 칸·외주 선택 블록·서약 체크를 그리지도 저장하지도 않는다(법무 §2-2 [상] 미고지 수집 금지). 제조사 허가번호 기존 칸은 시행 전 현행대로 users 에.
import { isPrivacyV335On } from '../config/privacyV335';
import { writeSignupVendorMetas, buildVendorPledges, vendorPledgeLabel, VENDOR_NUMBER_MAX, VENDOR_LICENSE_MAX } from '../services/signupVendorMeta';
import { MF_KINDS, MF_KIND_LABEL } from '../services/outsourceMfApi';   // 종류 라벨 재사용만(파일은 다른 작업자가 수정 중 — 수정 금지)
import { V70_ART4_7_TEXT_HASH } from '../config/termsV70';   // 📜 v7.0 시행 후 가입 동의 증빙에 제4조의7 문안 해시 동봉
// 🛡️ 치과 가입 필수 — 처리위탁 확인 2건(desk-clinic·pa-clinic) (2026-10-08 대표 "권고대로", 법무검토 §2 ⑤⑥ · §3 ☆ A, 계약_처리위탁_가입기록_서버)
import ClinicDelegationDocBox from './clinic/ClinicDelegationDocBox';
import { useSignupDelegationDocs, delegationCheckLabel, delegationFootnote, SIGNUP_DELEGATION_TITLE, signupDelegationIntro, BOX_MARK_HINT, SIGNUP_FAILED_KEY } from '../utils/clinicDelegation';
import { acceptSignupClinicDelegations } from '../services/clinicSignupDelegationApi';

function Register({ onRegister, onShowLogin, onClose, onSuccess }) {
    // 회원가입 페이지 방문 기록
    useEffect(() => {
        (async () => {
            try {
                let utm = null;
                try { utm = JSON.parse(sessionStorage.getItem('dcUtm') || 'null'); } catch (_) {}
                await addDoc(collection(db, 'signupVisits'), {
                    visitedAt: serverTimestamp(),
                    referrer: document.referrer || '',
                    page: '/signup',
                    ...(utm ? { utm } : {}),   // 유입 채널 (utm_source/medium/campaign + 랜딩 경로)
                });
            } catch (e) { /* ignore */ }
        })();
    }, []);

    const [step, setStep] = useState(1); // 1: 유형 선택, 2: 정보 입력
    const [userType, setUserType] = useState(''); // 'individual', 'dental', 'lab', 'outsource', 'seller'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedOutsourceCategories, setSelectedOutsourceCategories] = useState([]);
    // 🏭 제조사 자격(문헌 조사 10-23 — 맞춤 지대주 규제): 의료기기 제조업 허가 또는 치과기공소 개설 등록 중 하나 + 번호, 프리밀드 블랭크 사용 여부. 관리자가 서류 확인 후 승인
    const [vendorQual, setVendorQual] = useState({ type: '', number: '', premill: false });
    // 🏭 S5 — 기공소 「기공소 확인」: 면허번호·성명(입력 권장, 미입력 차단 안 함) · 다른 기공소 외주 수주 선택 · 종류 · 개설등록번호. 번호는 users 에 넣지 않는다(vendorDocs 메타).
    const [labCheck, setLabCheck] = useState({ licenseNo: '', licenseName: '', wants: false, kinds: [], openingNo: '' });
    const [agreedToPledge, setAgreedToPledge] = useState(false);   // 「외주 수주 의무 확인(필수)」 — 제조사, 기공소는 wants 일 때만
    const [termsV80Live, setTermsV80Live] = useState(false);       // 약관 v8.0 시행 여부(features 날짜) — 서약 라벨·pledgeVersion 분기(법무 §1-4)
    const v335On = isPrivacyV335On();                               // 처리방침 v3.3.5 시행 뒤에만 번호 칸·외주 선택·서약 체크
    const needPledge = v335On && (userType === 'outsource' || (userType === 'lab' && labCheck.wants));
    const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [agreedToAge, setAgreedToAge] = useState(false);
    const [agreedToMarketing, setAgreedToMarketing] = useState(false);
    // 위치기반 동의 상태 제거됨 (2026-08-06) — 가입 필수 폐지, 사용 시점 동의로 전환 (약관 원문: LocationTerms.js)
    const [agreedToDelegation, setAgreedToDelegation] = useState(false); // 환자정보 처리위탁·재위탁 (v5.0)
    // 🛡️ 치과만 — 데스크 접수·환자 앱 등록 처리위탁 확인(체크 1개, 문서별 서버 기록 2건). 웹 원문·서버 현재 판이 맞는 문서만(null=대조 중)
    const [agreedToClinicDelegation, setAgreedToClinicDelegation] = useState(false);
    const clinicDelegationDocs = useSignupDelegationDocs(userType === 'dental');
    const needClinicDelegation = userType === 'dental' && Array.isArray(clinicDelegationDocs) && clinicDelegationDocs.length > 0;
    // 구 수수료 모델 잔존 (사용 안 함, 호환용)
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const [agreedToFee, setAgreedToFee] = useState(false);
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const [agreedToProtection, setAgreedToProtection] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [bizVerified, setBizVerified] = useState(false);
    const [bizVerifying, setBizVerifying] = useState(false);
    const [bizVerifyResult, setBizVerifyResult] = useState(null);

    // 🦷 치과 초대 (features.clinicInvite) — ?invite=코드 로 진입한 치과 가입 처리
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const [clinicInviteOn, setClinicInviteOn] = useState(false);   // 플래그 OFF면 현행 동작 그대로
    // 전면 무료(allFree) 분기 제거 (약관 v6.2) — 신규 가입 화면에 "전면 무료"와 유료 요금을 함께 보이지 않음(법무 P1 표시광고)
    // 📜 약관 v7.0 시행 여부(2026-11-06 00:00 KST, config/features.termsV70At) — 동의 증빙 버전을 시행 시각 이후에만 v7.0·v3.2.5 로 기록.
    //   플래그를 못 읽어도 isTermsV70Effective({}) 가 기본 시행일로 판정(TermsOfService 와 같은 폴백) — 시행 후 v6.2 로 기록되는 일이 없게.
    const [termsV70Live, setTermsV70Live] = useState(() => isTermsV70Effective({}));
    const [partnerOn, setPartnerOn] = useState(false);   // 🏭 제조사 가입 카드 — config/features.partnerHome ON 때만(10-23 시뮬레이션: 서류 업로드·관리자 확인 흐름 전 노출 금지)
    const [inviteCode, setInviteCode] = useState('');
    const [inviteData, setInviteData] = useState(null);            // 유효한 초대 문서 (active + 미만료)

    useEffect(() => {
        let alive = true;
        // 플래그 로드 (비로그인이라 실패할 수 있음 — 실패 시 기본 false = 현행 동작)
        getFeatures()
            .then(f => {
                if (!alive) return;
                setClinicInviteOn(f?.clinicInvite === true); setTermsV70Live(isTermsV70Effective(f || {}));
                setTermsV80Live(isTermsV80Effective(f || {}));
                const pOn = f?.partnerHome === true;
                setPartnerOn(pOn);
                // 🏭 ?type=manufacturer → 제조사 프리셋 — 스위치 ON 때만 (OFF면 유형 선택 화면 그대로)
                if (pOn && (new URLSearchParams(window.location.search).get('type') || '').trim().toLowerCase() === 'manufacturer') {
                    setUserType('outsource');
                    setSelectedOutsourceCategories(['custom_milling']);
                    setStep(2);
                }
            })
            .catch(e => { console.error('[가입] 플래그 읽기 실패 — 기본값 사용:', e?.message || e); });

        // URL의 초대 코드 검증
        const params = new URLSearchParams(window.location.search);

        // 랜딩(/lab 등)에서 ?type=lab 으로 진입 → 기공소 유형 프리셋 + 정보 입력 단계 직행 (초대 프리셋과 동일 패턴)
        if ((params.get('type') || '').trim().toLowerCase() === 'lab') {
            setUserType('lab');
            setStep(2);
        }
        // 🤝 ?ref=코드 → 제조사 영업업체가 보낸 링크로 들어온 기공소 — 영업 추천코드 미리 채움
        const refCode = (params.get('ref') || '').trim().toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 20);
        if (refCode) setFormData(prev => ({ ...prev, salesRefCode: refCode }));
        const mfr = (params.get('mfr') || '').trim().replace(/[^A-Za-z0-9_-]/g, '').slice(0, 128);
        if (mfr) setFormData(prev => ({ ...prev, inviteMfr: mfr }));
        // 🏭 ?type=manufacturer 프리셋은 위 getFeatures() 결과(partnerHome)로 처리 — 저장은 기존 외주업체(outsource) 유형 그대로

        const code = (params.get('invite') || '').trim().toUpperCase();
        if (!code) return;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'clinicInvites', code));
                if (!alive || !snap.exists()) return;
                const data = snap.data();
                const expMs = data.expiresAt?.toMillis ? data.expiresAt.toMillis() : (data.expiresAt ? new Date(data.expiresAt).getTime() : 0);
                const valid = data.status === 'active' && (!expMs || expMs > Date.now());
                if (valid) {
                    setInviteCode(code);
                    setInviteData(data);
                    // 초대 링크 진입: 치과 유형 프리셋 후 바로 정보 입력 단계로
                    setUserType('dental');
                    setStep(2);
                }
            } catch (e) {
                console.warn('초대 코드 확인 실패:', e);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 폼 데이터
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        phone: '',
        businessName: '',
        businessNumber: '',
        // 주소 (Daum 우편번호 위젯 응답 — 수정 불가)
        address: '',           // 도로명 주소 (UI 표시용)
        postcode: '',
        roadAddress: '',
        jibunAddress: '',
        sido: '',
        sigungu: '',
        bname: '',             // 법정동
        buildingName: '',
        // 사용자 직접 입력
        detailAddress: '',     // 상세주소 (3층 301호 등)
        // 매칭 키 (백엔드 hospital-matcher와 동일 형식)
        _matchSido: '',
        _matchSggu: '',
        _matchDong: '',
        referrerPhone: '',
        salesRefCode: '',      // 🤝 영업 추천코드 — 제조사가 자기 영업업체에 준 코드(기공소 가입 시 선택, 2026-10-23)
        inviteMfr: '',         // 🏭 초대 링크로 지정된 제조사(?mfr=) — 제조사 직접 유치 또는 영업업체가 지정한 제조사
    });

    // 주소 검색 위젯 토글
    const [showAddressPicker, setShowAddressPicker] = useState(false);
    const handleAddressComplete = (data) => {
        setFormData(prev => ({
            ...prev,
            address: data.roadAddress,
            postcode: data.postcode || '',
            roadAddress: data.roadAddress || '',
            jibunAddress: data.jibunAddress || '',
            sido: data.sido || '',
            sigungu: data.sigungu || '',
            bname: data.bname || '',
            buildingName: data.buildingName || '',
            _matchSido: data._matchSido || '',
            _matchSggu: data._matchSggu || '',
            _matchDong: data._matchDong || '',
        }));
        setShowAddressPicker(false);
    };

    // 추천인 검증 결과
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const [referrerVerifying, setReferrerVerifying] = useState(false);
    const [referrerInfo, setReferrerInfo] = useState(null); // { uid, name, businessName, email, phone }
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const [referrerError, setReferrerError] = useState('');

    // 전화번호 정규화 (숫자만)
    const normalizePhone = (p) => (p || '').replace(/[^0-9]/g, '');

    // 추천인 전화번호 검증 (Cloud Function 호출 — 비로그인에서 안전하게 users 조회)
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const verifyReferrer = async () => {
        const target = normalizePhone(formData.referrerPhone);
        if (!target) { setReferrerError('추천인 전화번호를 입력해주세요.'); setReferrerInfo(null); return; }
        if (target.length < 9 || target.length > 11) { setReferrerError('전화번호 형식을 확인해주세요.'); setReferrerInfo(null); return; }
        setReferrerVerifying(true); setReferrerError('');
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const verify = httpsCallable(fns, 'verifyReferrer');
            const res = await verify({ phone: formData.referrerPhone });
            const data = res.data || {};
            if (data.found && data.referrer) {
                setReferrerInfo(data.referrer);
                setReferrerError('');
            } else {
                setReferrerInfo(null);
                setReferrerError('해당 전화번호로 가입된 회원이 없습니다.');
            }
        } catch (e) {
            console.error('추천인 검증 오류:', e);
            setReferrerInfo(null);
            setReferrerError(e?.message || '추천인 확인 중 오류가 발생했습니다.');
        }
        setReferrerVerifying(false);
    };

    // 사업자번호 확인
    const BIZ_API_DOWN_MSG = '국세청 확인이 일시적으로 되지 않습니다. 잠시 후 다시 시도해 주세요. 계속 안 되면 010-2972-3733으로 연락 주세요';
    const verifyBusinessNumber = async () => {
        const bNum = formData.businessNumber.replace(/[^0-9]/g, '');
        if (!bNum) { setError('사업자등록번호를 입력해주세요.'); return; }
        if (bNum.length !== 10) { setError('사업자등록번호는 10자리입니다.'); return; }
        setBizVerifying(true); setError('');
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const verify = httpsCallable(fns, 'verifyBusinessNumber');
            const res = await verify({ businessNumber: bNum });
            const data = res.data || {};
            if (data.apiConnected === false) {
                // 국세청 API 오류 — 서버가 확인하지 못함. 체크섬 폴백 없이 가입 차단 (2026-09-15 보안수정)
                console.error('사업자번호 확인: 국세청 API 미연결', data);
                setBizVerified(false);
                setBizVerifyResult({ valid: false, apiConnected: false, message: BIZ_API_DOWN_MSG });
                setError(BIZ_API_DOWN_MSG);
            } else {
                setBizVerifyResult(data);
                setBizVerified(data.valid === true);
                if (data.valid !== true) setError(data.message || '확인되지 않은 사업자번호입니다.');
            }
        } catch (e) {
            // CF 호출 자체 실패(네트워크 등) — 클라이언트 체크섬으로 통과시키지 않는다 (2026-09-15 보안수정)
            console.error('사업자번호 확인 호출 실패:', e);
            setBizVerified(false);
            setBizVerifyResult({ valid: false, apiConnected: false, message: BIZ_API_DOWN_MSG });
            setError(BIZ_API_DOWN_MSG);
        }
        setBizVerifying(false);
    };

    // Step 1: 회원 유형 선택
    const handleUserTypeSelect = (type) => {
        // 치과 공개 가입 허용 (2026-08-21 대표 지시: "초대가 아니더라도 회원가입 가능하게")
        // — 종전 clinicInvite 플래그 ON 시 초대 필수 차단을 해제. 초대 링크(?invite=)로 오면
        //   기존대로 자동 거래처 연결 등 혜택이 붙고, 없이 와도 가입은 된다.
        setUserType(type);
        // 제조사 카드 = 외주업체(outsource) 유형 + Custom Milling 기본 선택 (2026-10-23)
        if (type === 'outsource') setSelectedOutsourceCategories(prev => (prev.length ? prev : ['custom_milling']));
        setStep(2);
    };

    // 입력 값 변경
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // 회원가입 처리
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // 동의 확인 (필수)
        if (!agreedToPrivacy || !agreedToTerms || !agreedToAge || !agreedToDelegation) {
            setError('필수 약관(개인정보·이용약관·환자정보 처리위탁·만 14세 이상)에 모두 동의해주세요.');
            return;
        }
        // 🛡️ 치과: 처리위탁 확인 문서 대조가 끝나야 제출(대조 중 제출 → 문서가 빠진 채 가입되는 것 방지)
        if (userType === 'dental' && clinicDelegationDocs === null) {
            setError('확인 문서를 불러오는 중입니다. 잠시 후 다시 눌러 주세요.');
            return;
        }
        if (needClinicDelegation && !agreedToClinicDelegation) {
            setError('환자 정보 처리위탁 확인(치과 회원)에 체크해 주세요.');
            return;
        }

        // 치과 초대 필수 차단 해제 (2026-08-21 대표 지시) — 초대 없이도 가입 허용
        // 업체 가입 시 사업자번호 확인 필수
        if (['dental', 'lab', 'outsource', 'seller'].includes(userType) && !bizVerified) {
            setError('사업자등록번호 확인이 필요합니다. [확인] 버튼을 눌러주세요.');
            return;
        }

        // 유효성 검사
        if (formData.password !== formData.confirmPassword) {
            setError('비밀번호가 일치하지 않습니다.');
            return;
        }

        if (formData.password.length < 6) {
            setError('비밀번호는 최소 6자 이상이어야 합니다.');
            return;
        }

        if ((userType === 'dental' || userType === 'lab' || userType === 'outsource' || userType === 'seller') && !formData.businessName) {
            setError('업체명을 입력해주세요.');
            return;
        }

        if (['dental', 'lab', 'outsource', 'seller'].includes(userType) && !formData.roadAddress) {
            setError('주소를 입력해주세요. [📍 주소 검색하기] 버튼을 눌러주세요.');
            return;
        }

        if (userType === 'outsource' && selectedOutsourceCategories.length === 0) {
            setError('외주 카테고리를 최소 1개 이상 선택해주세요.');
            return;
        }
        if (userType === 'outsource' && (!vendorQual.type || !vendorQual.number.trim())) {
            setError('제조사 자격(의료기기 제조업 허가 또는 치과기공소 개설 등록)과 번호를 입력해주세요.');
            return;
        }
        // 🏭 S5 — 외주 수주 선택(기공소)·서약(제조사, wants 기공소). 면허번호 미입력은 차단하지 않는다(명세 §3-2, 결정 5)
        const labWants = v335On && userType === 'lab' && labCheck.wants;
        if (labWants && labCheck.kinds.length === 0) {
            setError('받을 외주 종류를 1개 이상 선택해주세요.');
            return;
        }
        if (labWants && !labCheck.openingNo.trim()) {
            setError('치과기공소 개설등록번호를 입력해주세요. (다른 기공소의 외주를 받으려면 필수)');
            return;
        }
        if (needPledge && !agreedToPledge) {
            setError('「외주 수주 의무 확인」에 체크해주세요.');
            return;
        }

        setLoading(true);

        try {
            // 1. Firebase 인증으로 사용자 생성
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                formData.email,
                formData.password
            );
            const user = userCredential.user;

            // 2. Firestore에 사용자 정보 저장
            const consentTimestamp = new Date().toISOString();
            const metaJobs = {};   // 🏭 S5 — vendorDocs/{uid}/files 메타로 보낼 번호(license·opening·mfgPermit). users 문서보다 먼저 쓴다(rules 가 vendorIntent 본인 수정을 막으므로 실패 표식은 users 최초 저장에 넣어야 함)
            const userData = {
                email: formData.email,
                name: formData.name,
                phone: formData.phone,
                userType: userType, // 'individual', 'dental', 'lab', 'seller'
                createdAt: consentTimestamp,
                updatedAt: consentTimestamp,
                // 약관 동의 기록 (법적 증빙용)
                consents: {
                    // 📜 시행 시각(2026-11-06) 전 = v3.2.3 / v6.2 / v5.0 유지, 후 = v3.2.5 / v7.0 / v7.0(+제4조의7 해시). 서버 subscription-billing.js TERMS_VERSION 도 같은 시각에 'v7.0' 으로(dev-server).
                    privacy: { agreed: true, version: termsV70Live ? 'v3.2.5' : 'v3.2.3', agreedAt: consentTimestamp },  // 처리방침 v3.2.3 (결제 수탁자 토스페이먼츠) → v3.2.5 (덴클 케어, 시행 2026-11-06)
                    terms: { agreed: true, version: termsV70Live ? 'v7.0' : 'v6.2', agreedAt: consentTimestamp },  // v6.2 (구독료 개정) → v7.0 (환자관리·제4조의7 개정) — 동의 증빙 버전은 게시 중인 약관과 일치해야 함. 서버 onUserCreated가 이 값으로 요금표·requiresReconsent 판정(계획 S2 ⑤)
                    // 위치기반 동의는 가입 필수에서 폐지 (2026-08-06) — 거리순·지도 기능 사용 시점에 브라우저 위치 권한으로 동의받음
                    age14: { agreed: true, agreedAt: consentTimestamp },
                    marketing: { agreed: agreedToMarketing, agreedAt: agreedToMarketing ? consentTimestamp : null },
                    delegation: termsV70Live
                        ? { agreed: true, version: 'v7.0', agreedAt: consentTimestamp, textHash: V70_ART4_7_TEXT_HASH }  // 환자정보 처리위탁·재위탁 (약관 v7.0 제4조의7 전면 개정 — 재확인 모달과 같은 기록 형식)
                        : { agreed: true, version: 'v5.0', agreedAt: consentTimestamp }  // 환자정보 처리위탁·재위탁 (약관 v5.0 제4조의7)
                }
            };

            // 업체 회원 공통 주소 필드 (dental/lab/outsource/seller)
            const applyBusinessAddress = (data) => {
                data.address = formData.roadAddress || formData.address || '';
                data.postcode = formData.postcode || '';
                data.roadAddress = formData.roadAddress || '';
                data.jibunAddress = formData.jibunAddress || '';
                data.sido = formData.sido || '';
                data.sigungu = formData.sigungu || '';
                data.bname = formData.bname || '';
                data.buildingName = formData.buildingName || '';
                data.detailAddress = formData.detailAddress || '';
                data._matchSido = formData._matchSido || '';
                data._matchSggu = formData._matchSggu || '';
                data._matchDong = formData._matchDong || '';
                data.fullAddress = [formData.roadAddress, formData.detailAddress]
                    .filter(Boolean).join(' ');
            };

            // 판매자 회원인 경우 승인 대기 상태로 설정
            if (userType === 'seller') {
                userData.businessName = formData.businessName;
                userData.businessNumber = formData.businessNumber || '';
                applyBusinessAddress(userData);
                userData.businessType = 'seller';
                userData.role = 'owner';
                userData.sellerStatus = 'pending'; // 승인 대기
                userData.isSellerApproved = false;
                userData.sellerAppliedAt = new Date().toISOString();
            }
            // 외주업체인 경우
            else if (userType === 'outsource') {
                userData.businessName = formData.businessName;
                userData.businessNumber = formData.businessNumber || '';
                applyBusinessAddress(userData);
                userData.businessType = 'outsource';
                userData.role = 'owner';
                userData.staff = [];
                userData.outsourceCategories = selectedOutsourceCategories;
                // 🏭 제조사 자격 + 승인제(10-23) — 관리자 회원관리 「승인 대기」에서 승인(S5: callable adminApproveSignup 이 vendorProfiles 까지 생성)
                //   S5(처리방침 v3.3.5 시행 뒤): 번호는 users 에 넣지 않고 vendorDocs 메타(아래 metaJobs)로 — users 는 전 업체 회원이 읽음(rules:142-146). 시행 전: 현행대로 users 에(배포 호환)
                userData.vendorQualification = v335On
                    ? { type: vendorQual.type, premill: !!vendorQual.premill, numberProvided: true }
                    : { type: vendorQual.type, number: vendorQual.number.trim().slice(0, 60), premill: !!vendorQual.premill };
                userData.approvalStatus = 'pending';
                if (v335On) {
                    metaJobs.mfgPermit = vendorQual.type === 'device_mfr' ? vendorQual.number : '';
                    metaJobs.opening = vendorQual.type === 'dental_lab' ? vendorQual.number : '';
                    // 외주 의향 — 제조사는 항상 CNC 수주(명세 §4-2 ④ outsource → ['cnc'] 고정). metaFailed 표식을 둘 자리이기도 하다
                    userData.vendorIntent = { wants: true, kinds: ['cnc'], at: consentTimestamp };
                }
            }
            // 업체 회원(치과/기공소)인 경우 추가 정보
            else if (userType === 'dental' || userType === 'lab') {
                userData.businessName = formData.businessName;
                userData.businessNumber = formData.businessNumber || '';
                applyBusinessAddress(userData);
                userData.businessType = userType; // 'dental' or 'lab'
                userData.role = 'owner'; // 오너
                userData.staff = []; // 직원 목록
                // 🔐 가입 승인제 (2026-08-25 대표 확정): 업체 가입은 관리자 승인 후 이용
                //    필드 없음 = 승인됨 원칙이므로 신규 업체 가입에만 명시적으로 pending을 넣는다
                userData.approvalStatus = 'pending';
                // 🏭 S5 — 기공소 「기공소 확인」(처리방침 v3.3.5 시행 뒤): users.vendorIntent 에는 번호 없음. 면허·개설등록번호는 vendorDocs 메타(아래 metaJobs)
                if (userType === 'lab' && v335On) {
                    const lic = labCheck.licenseNo.trim();
                    userData.vendorIntent = {
                        wants: !!labCheck.wants,
                        kinds: labCheck.wants ? labCheck.kinds.filter(k => MF_KINDS.includes(k)) : [],
                        at: consentTimestamp,
                        ...(lic ? {} : { licenseMissing: true }),
                    };
                    if (lic) metaJobs.license = { licenseNo: lic, licenseName: labCheck.licenseName.trim() };
                    if (labCheck.wants) metaJobs.opening = labCheck.openingNo;
                }
            }
            // 🏭 S5 — 서약 「외주 수주 의무 확인」(법무 §1-4 저장 구조: 판·문안 해시·가입 시 약관 판). 제조사 + wants 기공소만
            if (needPledge && agreedToPledge) {
                userData.consents.vendorPledges = await buildVendorPledges({
                    termsV80Live, termsVersion: userData.consents.terms.version, agreedAt: consentTimestamp,
                });
            }

            // 🤝 영업 추천코드 — 기공소 가입 시 입력(또는 ?ref= 링크)된 경우만 기록. 제조사가 자기 영업업체 실적·수수료 정산에 사용
            if (userType === 'lab' && partnerOn && formData.salesRefCode) {
                userData.salesRefCode = String(formData.salesRefCode).trim().toUpperCase().slice(0, 20);
            }
            // 🏭 제조사 초대 링크(?mfr=)로 가입한 기공소 — 어느 제조사로 연결할지 기록(연결 처리는 다음 단계)
            if (userType === 'lab' && partnerOn && formData.inviteMfr) {
                userData.inviteMfr = formData.inviteMfr;
            }

            // 추천인 정보 (검증된 경우에만 저장)
            if (referrerInfo && referrerInfo.uid) {
                userData.referrer = {
                    uid: referrerInfo.uid,
                    phone: referrerInfo.phone,
                    name: referrerInfo.name,
                    businessName: referrerInfo.businessName,
                    email: referrerInfo.email,
                    enteredAt: consentTimestamp,
                };
            }

            // 유입 채널(UTM) 각인 — 채널별 가입 성과 측정용 (App.js가 세션에 저장한 값)
            try {
                const utm = JSON.parse(sessionStorage.getItem('dcUtm') || 'null');
                if (utm) userData.utm = utm;
            } catch (_) { /* 무시 */ }
            // 🏭 S5 — 번호 메타 쓰기(uid 확보 뒤·users 저장 전). 실패해도 가입은 진행하고 users.vendorIntent.metaFailed:true 로 표식(관리자 화면 「번호 기록 실패」 → 보류).
            //    ★ 실패 시 번호를 users 에 대신 저장하는 fallback 금지(법무 L9). 로그에 번호 없음(헬퍼가 type 만 찍음).
            if (Object.keys(metaJobs).length > 0) {
                const metaRes = await writeSignupVendorMetas(user.uid, metaJobs);
                if (!metaRes.ok) {
                    console.error('[가입] 외주 자격 메타 일부 실패 — users.vendorIntent.metaFailed 표식:', metaRes.failedTypes.join(','));
                    userData.vendorIntent = { ...(userData.vendorIntent || { wants: false, kinds: [], at: consentTimestamp }), metaFailed: true };
                }
            }
            await setDoc(doc(db, 'users', user.uid), userData);
            // 번호는 클라이언트 state 에도 남기지 않는다(법무 §3-3 ④)
            setVendorQual(p => ({ ...p, number: '' }));
            setLabCheck(p => ({ ...p, licenseNo: '', licenseName: '', openingNo: '' }));

            // 🛡️ 치과: 처리위탁 확인 2건 서버 기록(문서별, 가입 전용 경로 — 승인 대기 대표 허용). users 문서 저장 뒤에만 부른다(계약 §2-2).
            //    실패·부분 실패는 가입을 막지 않는다 — 콘솔 기록 + 표식(SIGNUP_FAILED_KEY)으로 승인 후 로그인 모달을 반드시 띄운다(조용한 실패 금지).
            if (needClinicDelegation && agreedToClinicDelegation) {
                const rec = await acceptSignupClinicDelegations(clinicDelegationDocs);
                if (!rec.ok) {
                    console.error('[가입] 처리위탁 확인 서버 기록 미완료 — 로그인 후 확인 모달로 넘김:', rec.error || 'partial');
                    try { localStorage.setItem(SIGNUP_FAILED_KEY(user.uid), '1'); }
                    catch (e2) { console.error('[가입] 처리위탁 기록 실패 표식 저장 실패:', e2?.message || e2); }
                }
            }

            // 추천인 기록을 별도 컬렉션에 저장 (관리자가 선물 지급 처리용)
            if (referrerInfo && referrerInfo.uid) {
                try {
                    await addDoc(collection(db, 'referrals'), {
                        // 추천인 정보 (선물 받는 사람)
                        referrerUid: referrerInfo.uid,
                        referrerPhone: referrerInfo.phone,
                        referrerName: referrerInfo.name || '',
                        referrerBusinessName: referrerInfo.businessName || '',
                        referrerEmail: referrerInfo.email || '',
                        // 신규 가입자 정보 (추천 받아 가입한 사람)
                        newUserUid: user.uid,
                        newUserPhone: formData.phone,
                        newUserName: formData.name || '',
                        newUserBusinessName: formData.businessName || '',
                        newUserEmail: formData.email,
                        newUserType: userType,
                        // 선물 지급 상태
                        giftStatus: 'pending', // 'pending' | 'sent' | 'cancelled'
                        giftSentAt: null,
                        adminNotes: '',
                        createdAt: serverTimestamp(),
                    });
                } catch (refErr) {
                    console.error('추천 기록 저장 실패:', refErr);
                }
            }

            // 3. 치과 회원: 자동 매칭 후보 검색 (Phase C 진입점)
            //    findHospitalCandidates Cloud Function 호출 → 결과를 sessionStorage에 저장
            //    → 첫 로그인 시 후보 선택 모달 자동 노출 (Phase C에서 처리)
            if (userType === 'dental' && formData.businessName) {
                try {
                    const fns = getFunctions(undefined, 'asia-northeast3');
                    const findCands = httpsCallable(fns, 'findHospitalCandidates');
                    const res = await findCands({
                        businessName: formData.businessName,
                        address: formData.roadAddress || formData.address || '',
                        jibunAddress: formData.jibunAddress || '',
                        phone: formData.phone || '',
                    });
                    const candidates = res.data?.candidates || [];
                    sessionStorage.setItem('hospitalMatchCandidates', JSON.stringify(candidates));
                    sessionStorage.setItem('hospitalMatchPending', 'true');
                } catch (matchErr) {
                    console.warn('자동 매칭 후보 검색 실패 (가입은 정상):', matchErr.message);
                }
            }

            // 3.5 🦷 초대 코드 가입 (치과): 초대한 기공소와 자동 거래처 연결 + 초대 사용 처리
            //     실패해도 가입은 성공 처리 (연결은 이후 거래처 관리에서 수동 가능)
            if (userType === 'dental' && inviteData && inviteCode) {
                try {
                    // 기존 connections 스키마 (ConnectionList.js와 동일): 쌍 고정 ID로 중복 방지
                    const connId = [user.uid, inviteData.labId].sort().join('_');
                    await setDoc(doc(db, 'connections', connId), {
                        requesterId: user.uid,
                        receiverId: inviteData.labId,
                        recipientId: inviteData.labId,
                        requesterName: formData.businessName || formData.name || '',
                        requesterType: 'dental',
                        receiverName: inviteData.labName || '',
                        status: 'accepted',
                        source: 'clinic_invite',
                        createdAt: new Date(),
                    }, { merge: true });
                } catch (connErr) {
                    console.error('초대 자동 연결 실패 (가입은 정상):', connErr);
                }
                try {
                    // 초대 사용 처리 (rules: usedBy/usedAt/status만, 본인 uid만 허용)
                    await updateDoc(doc(db, 'clinicInvites', inviteCode), {
                        usedBy: user.uid,
                        usedAt: new Date(),
                        status: 'used',
                    });
                } catch (useErr) {
                    console.error('초대 사용 처리 실패 (가입은 정상):', useErr);
                }
                // 착지: 초대 가입 치과는 /create-order (App.js /signup 리다이렉트에서 사용)
                try { sessionStorage.setItem('clinicInviteSignup', '1'); } catch (e) { /* ignore */ }
            }

            // 4. 회원가입 성공
            if (onRegister) onRegister(user);
            if (onSuccess) onSuccess();

            if (userType === 'seller') {
                alert('판매자 회원 가입이 완료되었습니다!\n관리자 승인 후 이용하실 수 있습니다.');
            } else if (userType === 'dental' || userType === 'lab' || userType === 'outsource') {
                // 🔐 가입 승인제 (2026-08-25 대표 확정) — 제조사도 자격 확인 후 이용(2026-10-23)
                alert('회원가입이 완료되었습니다!\n관리자 승인 후 이용하실 수 있습니다. (보통 1영업일 이내)');
            } else {
                alert('회원가입이 완료되었습니다!');
            }

        } catch (error) {
            console.error('회원가입 오류:', error);

            let errorMessage = '회원가입에 실패했습니다.';
            if (error.code === 'auth/email-already-in-use') {
                errorMessage = '이미 사용 중인 이메일입니다.';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = '유효하지 않은 이메일 형식입니다.';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = '비밀번호가 너무 약합니다. (최소 6자)';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // 뒤로 가기
    const handleBack = () => {
        if (step === 2) {
            setUserType('');
            setStep(1);
        }
    };

    return (
        <div style={styles.container}>
            {/* 개인정보보호방침 모달 */}
            {showPrivacyModal && (
                <PrivacyPolicy onClose={() => setShowPrivacyModal(false)} />
            )}

            {/* 서비스 이용약관 모달 (단일 소스) */}
            {showTermsModal && (
                <TermsOfService onClose={() => setShowTermsModal(false)} />
            )}

            {/* 위치기반서비스 이용약관: 가입 필수 동의 폐지(2026-08-06)로 모달 제거 — 원문은 LocationTerms.js(/location-terms)로 이관 */}

            <div className="register-box" style={styles.box}>
                {/* 🦷 초대 가입 배너 */}
                {inviteData && (
                    <div style={{
                        padding: '12px 16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                        borderRadius: 10, marginBottom: 20, fontSize: 13, color: '#166534', lineHeight: 1.6,
                    }}>
                        🦷 <b>{inviteData.labName || '기공소'}</b>의 초대로 가입합니다 — 가입하면 자동으로 거래처가 연결됩니다
                    </div>
                )}

                {/* 헤더 */}
                <div style={styles.header}>
                    {step === 2 && (
                        <button
                            onClick={handleBack}
                            style={styles.backButton}
                            type="button"
                        >
                            <ArrowLeft size={20} />
                        </button>
                    )}
                    <Building2 size={40} style={{ color: '#6366f1' }} />
                    <h1 style={styles.title}>회원가입</h1>
                    <p style={styles.subtitle}>
                        {step === 1 && '회원 유형을 선택해주세요'}
                        {step === 2 && '정보를 입력해주세요'}
                    </p>
                </div>

                {/* Step 1: 회원 유형 선택 — 컨시어지 MVP: 기공소 중심 (직원/외주/판매자 임시 숨김, 2026-07-14) */}
                {step === 1 && (
                    <div className="type-selection" style={styles.typeSelection}>
                        <div
                            className="type-card"
                            style={{ ...styles.typeCard, border: '2px solid #f59e0b' }}
                            onClick={() => handleUserTypeSelect('lab')}
                        >
                            <Microscope size={32} style={{ color: '#f59e0b' }} />
                            <h3 style={styles.typeTitle}>기공소</h3>
                            <p style={styles.typeDesc}>
                                치과 기공소를<br />
                                운영하는 업체로 가입합니다
                            </p>
                            <div style={{ ...styles.typeBadge, backgroundColor: '#fef3c7', color: '#92400e' }}>
                                {'거래명세서 자동 작성 · 1개월 무료 체험'}
                            </div>
                        </div>

                        <div
                            className="type-card"
                            style={styles.typeCard}
                            onClick={() => handleUserTypeSelect('dental')}
                        >
                            <Stethoscope size={32} style={{ color: '#10b981' }} />
                            <h3 style={styles.typeTitle}>치과</h3>
                            <p style={styles.typeDesc}>
                                치과 의원을<br />
                                운영하는 업체로 가입합니다
                            </p>
                            <div style={{ ...styles.typeBadge, backgroundColor: '#d1fae5', color: '#065f46' }}>
                                재제작 상담 신청은 가입 없이 가능
                            </div>
                        </div>

                        {/* 🏭 제조사(CNC 밀링센터) — 저장은 외주업체(outsource) 유형, partnerHome 스위치 ON 때만 노출 (2026-10-23) */}
                        {partnerOn && <div
                            className="type-card"
                            style={styles.typeCard}
                            onClick={() => handleUserTypeSelect('outsource')}
                        >
                            <Factory size={32} style={{ color: '#6366f1' }} />
                            <h3 style={styles.typeTitle}>제조사</h3>
                            <p style={styles.typeDesc}>
                                커스텀 어버트먼트 등을 가공하는<br />
                                CNC 밀링센터로 가입합니다
                            </p>
                            <div style={{ ...styles.typeBadge, backgroundColor: '#e0e7ff', color: '#3730a3' }}>
                                {'기공소 케이스 접수 · 지그 파일 함께 받기'}
                            </div>
                        </div>}
                    </div>
                )}

                {/* Step 2: 정보 입력 */}
                {step === 2 && (
                    <form onSubmit={handleSubmit} style={styles.form}>
                        {/* 에러 메시지 */}
                        {error && (
                            <div style={styles.error}>
                                {error}
                            </div>
                        )}

                        {/* 기본 정보 */}
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>
                                기본 정보
                                {userType === 'individual' && (
                                    <span style={styles.businessTypeLabel}>개인</span>
                                )}
                                {userType === 'dental' && (
                                    <span style={{ ...styles.businessTypeLabel, backgroundColor: '#d1fae5', color: '#065f46' }}>치과</span>
                                )}
                                {userType === 'lab' && (
                                    <span style={{ ...styles.businessTypeLabel, backgroundColor: '#fef3c7', color: '#92400e' }}>기공소</span>
                                )}
                                {userType === 'outsource' && (
                                    <span style={{ ...styles.businessTypeLabel, backgroundColor: '#e0e7ff', color: '#3730a3' }}>제조사</span>
                                )}
                                {userType === 'seller' && (
                                    <span style={{ ...styles.businessTypeLabel, backgroundColor: '#fce7f3', color: '#9d174d' }}>판매자</span>
                                )}
                            </h3>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>이메일 *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="example@email.com"
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>비밀번호 *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="최소 6자 이상"
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>비밀번호 확인 *</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="비밀번호 재입력"
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>이름 *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="홍길동"
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>전화번호 *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="010-1234-5678"
                                    style={styles.input}
                                    required
                                />
                            </div>

                            {/* 추천인 항목 제거 (2026-07-14 사용자 결정) */}
                            {/* 🤝 영업 추천코드 — 기공소만, 선택 입력 (2026-10-23) */}
                            {userType === 'lab' && partnerOn && (
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>영업 추천코드 <span style={{ color: '#94a3b8', fontWeight: 400 }}>(선택)</span></label>
                                    <input
                                        type="text"
                                        name="salesRefCode"
                                        value={formData.salesRefCode}
                                        onChange={(e) => setFormData(prev => ({ ...prev, salesRefCode: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 20) }))}
                                        placeholder="제조사 영업 담당에게 받은 코드가 있으면 입력하세요"
                                        style={styles.input}
                                    />
                                </div>
                            )}
                        </div>

                        {/* 기공소 요금 고지 — 거래명세서 서비스 기준 (2026-07-23 개정) */}
                        {userType === 'lab' && (
                            <div style={{ background: '#f5f6f8', border: '1px solid #e6e8ec', borderLeft: '3px solid #274690', borderRadius: 10, padding: '13px 16px', marginBottom: 18, fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                                <b style={{ color: '#151b26' }}>이용 요금 안내 — 거래명세서 자동화 서비스</b><br />
                                · 가입은 <b>무료</b>이며, 가입만으로는 어떠한 비용도 발생하지 않습니다.<br />
                                · 덴클 에이전트가 CAD 작업 폴더를 자동 분석해 <b>월말 거래명세서·세금계산서를 자동으로</b> 만들어드립니다.<br />
                                · 가입 즉시 <b>1개월 무료 체험</b>이 시작됩니다 (사업자당 1회, 카드 등록 불필요). 체험 후 계속 이용 시 <b>월 33,000원(부가세 포함)</b>입니다.<br />
                                · 결제는 별도의 결제 동의 후에만 이루어지며, 언제든 해지할 수 있습니다.<br />
                                · 매칭·중개 등 추가 서비스 오픈 시 요금은 이용약관에 따라 별도 안내됩니다.
                            </div>
                        )}

                        {/* 🏭 S5 — 기공소 「기공소 확인」 블록 (처리방침 v3.3.5 시행 2026-11-03 KST 뒤에만, 법무 §2-2). 면허번호·성명 입력 권장(차단 안 함) · 외주 수주 선택 · 종류 · 개설등록번호. 안내 문구 법무 §2-6②·§5-3② 글자 그대로, CNC 안내 명세 §3-2 */}
                        {userType === 'lab' && v335On && (
                            <div style={{ background: '#fff', border: '1px solid #e6e8ec', borderLeft: '3px solid #f59e0b', borderRadius: 10, padding: '13px 16px', marginBottom: 18, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                                <b style={{ color: '#151b26', fontSize: 14 }}>{'기공소 확인'}</b>
                                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                    <div style={{ ...styles.inputGroup, flex: '1 1 180px' }}>
                                        <label style={styles.label}>{'치과기공사 면허번호 '}<span style={{ color: '#b45309', fontWeight: 600 }}>{'(입력 권장)'}</span></label>
                                        <input type="text" value={labCheck.licenseNo} maxLength={VENDOR_LICENSE_MAX} autoComplete="off"
                                            onChange={(e) => setLabCheck(p => ({ ...p, licenseNo: e.target.value.slice(0, VENDOR_LICENSE_MAX) }))}
                                            placeholder="면허번호" style={styles.input} />
                                    </div>
                                    <div style={{ ...styles.inputGroup, flex: '1 1 140px' }}>
                                        <label style={styles.label}>{'면허 소지자 성명'}</label>
                                        <input type="text" value={labCheck.licenseName} maxLength={VENDOR_LICENSE_MAX} autoComplete="off"
                                            onChange={(e) => setLabCheck(p => ({ ...p, licenseName: e.target.value.slice(0, VENDOR_LICENSE_MAX) }))}
                                            placeholder="성명" style={styles.input} />
                                    </div>
                                </div>
                                <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                                    {'개설자 또는 소속 치과기공사 1명. 가입자 본인과 달라도 됩니다. 본인이 아닌 치과기공사의 번호를 입력할 때는 그 기공사에게 덴클에 제공한다는 사실을 알린 뒤 입력하세요. 면허번호는 본인과 덴클 관리자만 볼 수 있습니다.'}
                                </div>
                                <label style={{ ...styles.checkboxLabel, marginTop: 12, alignItems: 'center' }}>
                                    <input type="checkbox" checked={labCheck.wants} style={styles.checkbox}
                                        onChange={(e) => { const v = e.target.checked; setLabCheck(p => ({ ...p, wants: v })); if (!v) setAgreedToPledge(false); }} />
                                    <span style={{ ...styles.checkboxText, fontWeight: 600, color: '#151b26' }}>{'다른 기공소의 외주도 받겠습니까?'}</span>
                                </label>
                                <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                                    {'외주 제작은 설계 파일을 바탕으로 한 공정 일부(금속·지르코니아 구조물, 지그 등)만 받습니다. 최종 기공물의 완성·치과 납품 책임은 의뢰한 기공소에 있고, 결과물은 의뢰한 기공소로만 보냅니다.'}
                                </div>
                                {labCheck.wants && (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e6e8ec' }}>
                                        <label style={styles.label}>{'받을 외주 종류 '}<span style={{ color: '#ef4444' }}>*</span></label>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                                            {MF_KINDS.map(k => (
                                                <label key={k} style={{
                                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', fontSize: 13, cursor: 'pointer',
                                                    border: `1px solid ${labCheck.kinds.includes(k) ? '#f59e0b' : '#e2e8f0'}`, borderRadius: 8, backgroundColor: labCheck.kinds.includes(k) ? '#fffbeb' : '#fff',
                                                }}>
                                                    <input type="checkbox" checked={labCheck.kinds.includes(k)}
                                                        onChange={() => setLabCheck(p => ({ ...p, kinds: p.kinds.includes(k) ? p.kinds.filter(x => x !== k) : [...p.kinds, k] }))} />
                                                    {MF_KIND_LABEL[k]}
                                                </label>
                                            ))}
                                        </div>
                                        {labCheck.kinds.includes('cnc') && (
                                            <div style={{ marginTop: 6, fontSize: 12, color: '#b45309', lineHeight: 1.6 }}>
                                                {'CNC는 의료기기 제조업 허가가 있는 기공소만 — 관리자가 확인 전까지 CNC만 보류'}
                                            </div>
                                        )}
                                        <div style={{ ...styles.inputGroup, marginTop: 10 }}>
                                            <label style={styles.label}>{'치과기공소 개설등록번호 '}<span style={{ color: '#ef4444' }}>*</span></label>
                                            <input type="text" value={labCheck.openingNo} maxLength={VENDOR_NUMBER_MAX} autoComplete="off"
                                                onChange={(e) => setLabCheck(p => ({ ...p, openingNo: e.target.value.slice(0, VENDOR_NUMBER_MAX) }))}
                                                placeholder="개설등록번호" style={styles.input} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 치과 요금 고지 — 약관 v6.2 (법무검토 ④ 가입 화면 치과 요금 상자, 약관규제법 §3 중요 내용 명시) */}
                        {userType === 'dental' && (
                            <div style={{ background: '#f5f6f8', border: '1px solid #e6e8ec', borderLeft: '3px solid #10b981', borderRadius: 10, padding: '13px 16px', marginBottom: 18, fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                                <b style={{ color: '#151b26' }}>{'이용 요금 안내'}</b><br />
                                {/* 2026-10-30 대표 결정: 치과는 무료 — 99,000원 구독 안내 삭제 */}
                                {'· 치과 회원은 '}<b>{'무료'}</b>{'로 이용합니다. 가입·이용에 비용이 발생하지 않습니다.'}<br />
                                {'· 요금이 생기게 되면 최소 30일 전에 개별 안내하고, 결제 동의 없이는 청구하지 않습니다.'}
                            </div>
                        )}

                        {/* 업체 정보 (업체 회원만) */}
                        {(userType === 'dental' || userType === 'lab' || userType === 'outsource' || userType === 'seller') && (
                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>업체 정보</h3>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>업체명 *</label>
                                    <input
                                        type="text"
                                        name="businessName"
                                        value={formData.businessName}
                                        onChange={handleChange}
                                        placeholder={
                                            userType === 'dental' ? '○○치과' :
                                            userType === 'lab' ? '○○기공소' :
                                            userType === 'outsource' ? '○○밀링센터' :
                                            '○○메디컬 / ○○덴탈'
                                        }
                                        style={styles.input}
                                        required
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>사업자등록번호 <span style={{ color: '#ef4444' }}>*</span></label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="text"
                                            name="businessNumber"
                                            value={formData.businessNumber}
                                            onChange={(e) => { handleChange(e); setBizVerified(false); setBizVerifyResult(null); }}
                                            placeholder="000-00-00000"
                                            style={{ ...styles.input, flex: 1 }}
                                            disabled={bizVerified}
                                        />
                                        <button
                                            type="button"
                                            onClick={bizVerified ? () => { setBizVerified(false); setBizVerifyResult(null); } : verifyBusinessNumber}
                                            disabled={bizVerifying}
                                            style={{
                                                padding: '10px 16px', border: 'none', borderRadius: 8,
                                                backgroundColor: bizVerified ? '#10b981' : '#6366f1',
                                                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                                whiteSpace: 'nowrap', opacity: bizVerifying ? 0.6 : 1,
                                            }}
                                        >
                                            {bizVerifying ? '확인 중...' : bizVerified ? '✓ 확인됨' : '확인'}
                                        </button>
                                    </div>
                                    {bizVerifyResult && (
                                        <div style={{
                                            marginTop: 6, padding: '6px 10px', borderRadius: 6, fontSize: 12,
                                            backgroundColor: bizVerifyResult.valid ? '#f0fdf4' : '#fef2f2',
                                            color: bizVerifyResult.valid ? '#16a34a' : '#dc2626',
                                            display: 'flex', alignItems: 'center', gap: 6,
                                        }}>
                                            {bizVerifyResult.valid ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                                            {bizVerifyResult.message}
                                        </div>
                                    )}
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>주소 <span style={{ color: '#dc2626' }}>*</span></label>
                                    {!formData.roadAddress ? (
                                        <button
                                            type="button"
                                            onClick={() => setShowAddressPicker(true)}
                                            style={{
                                                ...styles.input,
                                                cursor: 'pointer', textAlign: 'left',
                                                background: '#fff', color: '#64748b',
                                                display: 'flex', alignItems: 'center', gap: 6,
                                                border: '1px solid #f87171',
                                            }}
                                        >
                                            📍 주소 검색하기 (도로명·건물명·지번)
                                        </button>
                                    ) : (
                                        <div style={{
                                            border: '1px solid #cbd5e1', borderRadius: 8, padding: 12,
                                            background: '#f8fafc',
                                        }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                                {formData.roadAddress}
                                                {formData.buildingName && ` (${formData.buildingName})`}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                                지번: {formData.jibunAddress}
                                            </div>
                                            <input
                                                type="text"
                                                name="detailAddress"
                                                value={formData.detailAddress}
                                                onChange={handleChange}
                                                placeholder="상세주소 (예: 3층 301호)"
                                                style={{
                                                    ...styles.input,
                                                    marginTop: 8, padding: '8px 10px', fontSize: 13,
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowAddressPicker(true)}
                                                style={{
                                                    marginTop: 6, padding: '4px 10px', fontSize: 11,
                                                    background: 'transparent', color: '#0ea5e9',
                                                    border: '1px solid #0ea5e9', borderRadius: 6, cursor: 'pointer',
                                                }}
                                            >
                                                주소 변경
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* 🏭 제조사 자격 — 맞춤 지대주 등은 의료기기 제조 허가 또는 기공소 등록이 필요(10-23) */}
                                {userType === 'outsource' && (
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>제조사 자격 *</label>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {[
                                                { v: 'device_mfr', t: '의료기기 제조업 허가' },
                                                { v: 'dental_lab', t: '치과기공소 개설 등록' },
                                            ].map(o => (
                                                <label key={o.v} style={{
                                                    flex: '1 1 180px', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                                                    border: `1px solid ${vendorQual.type === o.v ? '#6366f1' : '#e2e8f0'}`, borderRadius: 8,
                                                    backgroundColor: vendorQual.type === o.v ? '#f5f3ff' : '#fff', cursor: 'pointer', fontSize: 13,
                                                }}>
                                                    <input type="radio" name="vendorQualType" checked={vendorQual.type === o.v} onChange={() => setVendorQual(p => ({ ...p, type: o.v }))} />
                                                    {o.t}
                                                </label>
                                            ))}
                                        </div>
                                        <input
                                            type="text"
                                            value={vendorQual.number}
                                            onChange={(e) => setVendorQual(p => ({ ...p, number: e.target.value }))}
                                            placeholder={vendorQual.type === 'dental_lab' ? '치과기공소 개설 등록번호' : '의료기기 제조업 허가번호'}
                                            style={{ ...styles.input, marginTop: 8 }}
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13, color: '#334155' }}>
                                            <input type="checkbox" checked={vendorQual.premill} onChange={(e) => setVendorQual(p => ({ ...p, premill: e.target.checked }))} />
                                            허가받은 프리밀드 블랭크를 사용합니다 (커스텀 어버트먼트)
                                        </label>
                                        <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                                            {'관리자가 허가번호를 식약처 공개 조회로 확인한 뒤 승인합니다. 확인이 안 되면 가입은 승인되되 외주 수주만 보류되며, 「외주 수주 설정」에서 허가증을 올리면 열립니다. GMP 적합 인정서는 CNC 수주 개시 전 설정 › 외주 수주 설정에서 제출이 필요합니다.'}
                                        </div>
                                    </div>
                                )}

                                {/* 외주업체 카테고리 선택 */}
                                {userType === 'outsource' && (
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>제작 분야 * (복수 선택 가능)</label>
                                        <div style={styles.categoryGrid}>
                                            {[
                                                { id: 'custom_milling', label: 'Custom Milling' },
                                                { id: '3d_printing', label: '3D Printing' },
                                                { id: 'design', label: 'Design' },
                                                { id: 'etc', label: '기타' }
                                            ].map(cat => (
                                                <div
                                                    key={cat.id}
                                                    onClick={() => {
                                                        setSelectedOutsourceCategories(prev =>
                                                            prev.includes(cat.id)
                                                                ? prev.filter(c => c !== cat.id)
                                                                : [...prev, cat.id]
                                                        );
                                                    }}
                                                    style={{
                                                        ...styles.categoryCard,
                                                        borderColor: selectedOutsourceCategories.includes(cat.id) ? '#8b5cf6' : '#e2e8f0',
                                                        backgroundColor: selectedOutsourceCategories.includes(cat.id) ? '#f5f3ff' : '#fff',
                                                    }}
                                                >
                                                    <span style={{
                                                        fontSize: '13px',
                                                        fontWeight: selectedOutsourceCategories.includes(cat.id) ? '600' : '400',
                                                        color: selectedOutsourceCategories.includes(cat.id) ? '#7c3aed' : '#64748b'
                                                    }}>{cat.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 약관 동의 */}
                        <div style={{ ...styles.agreementBox, padding: '16px', backgroundColor: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#0f172a' }}>약관 동의</div>

                            {/* 전체 동의 */}
                            <label style={{ ...styles.checkboxLabel, padding: '10px 12px', backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 10 }}>
                                <input type="checkbox"
                                    checked={agreedToPrivacy && agreedToTerms && agreedToAge && agreedToDelegation && agreedToMarketing && (!needClinicDelegation || agreedToClinicDelegation) && (!needPledge || agreedToPledge)}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setAgreedToPrivacy(v);
                                        setAgreedToTerms(v);
                                        setAgreedToAge(v);
                                        setAgreedToDelegation(v);
                                        setAgreedToMarketing(v);
                                        if (needClinicDelegation) setAgreedToClinicDelegation(v);   // 🛡️ 치과 처리위탁 확인도 전체 동의에 포함(개별 항목·전문은 계속 표시)
                                        if (needPledge) setAgreedToPledge(v);                       // 🏭 S5 외주 수주 의무 확인도 전체 동의에 포함
                                    }}
                                    style={styles.checkbox} />
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>전체 동의 (선택 항목 포함)</span>
                            </label>

                            {/* 만 14세 이상 */}
                            <label style={styles.checkboxLabel}>
                                <input type="checkbox" checked={agreedToAge} onChange={(e) => setAgreedToAge(e.target.checked)} style={styles.checkbox} />
                                <span style={styles.checkboxText}>
                                    만 14세 이상입니다 <span style={styles.required}>(필수)</span>
                                </span>
                            </label>

                            {/* 개인정보 */}
                            <label style={styles.checkboxLabel}>
                                <input type="checkbox" checked={agreedToPrivacy} onChange={(e) => setAgreedToPrivacy(e.target.checked)} style={styles.checkbox} />
                                <span style={styles.checkboxText}>
                                    <button type="button" onClick={() => setShowPrivacyModal(true)} style={styles.linkText}>개인정보 처리방침</button>
                                    에 동의합니다 <span style={styles.required}>(필수)</span>
                                </span>
                            </label>

                            {/* 이용약관 */}
                            <label style={styles.checkboxLabel}>
                                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} style={styles.checkbox} />
                                <span style={styles.checkboxText}>
                                    <button type="button" onClick={() => setShowTermsModal(true)} style={styles.linkText}>서비스 이용약관</button>
                                    에 동의합니다 <span style={styles.required}>(필수)</span>
                                </span>
                            </label>

                            {/* 위치기반 동의: 가입 필수 폐지 (2026-08-06) — 거리순·지도 기능 사용 시점 동의로 전환, 약관은 LocationTerms.js(/location-terms)에 게시 */}

                            {/* 환자정보 처리위탁·재위탁 (v5.0 — 키즈노트식 위탁계약 내장) */}
                            <label style={styles.checkboxLabel}>
                                <input type="checkbox" checked={agreedToDelegation} onChange={(e) => setAgreedToDelegation(e.target.checked)} style={styles.checkbox} />
                                <span style={styles.checkboxText}>
                                    환자 개인정보 처리위탁 및 CAD 설계 재위탁에 동의합니다
                                    (<button type="button" onClick={() => setShowTermsModal(true)} style={styles.linkText}>이용약관 제4조의7</button>)
                                    <span style={styles.required}> (필수)</span>
                                </span>
                            </label>

                            {/* 🛡️ 치과만 — 환자 정보 처리위탁 확인(☆ A). 문서 2건 전문 박스 + 체크 1개. 웹 원문·서버 현재 판이 맞지 않는 문서는 생략(승인 후 로그인 모달로) */}
                            {userType === 'dental' && clinicDelegationDocs === null && (
                                <div style={{ fontSize: 12.5, color: '#64748b', margin: '8px 0' }}>환자 정보 처리위탁 확인 문서를 불러오는 중…</div>
                            )}
                            {needClinicDelegation && (
                                <div style={{ margin: '10px 0', padding: '12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{SIGNUP_DELEGATION_TITLE}</div>
                                    <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.65, marginTop: 4 }}>{signupDelegationIntro(clinicDelegationDocs.length)}</div>
                                    {clinicDelegationDocs.map((d, i) => <ClinicDelegationDocBox key={d.key} no={i + 1} doc={d} />)}
                                    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginTop: 8 }}>{BOX_MARK_HINT}</div>
                                    <label style={{ ...styles.checkboxLabel, alignItems: 'flex-start', marginTop: 6 }}>
                                        <input type="checkbox" checked={agreedToClinicDelegation} onChange={(e) => setAgreedToClinicDelegation(e.target.checked)} style={styles.checkbox} />
                                        <span style={styles.checkboxText}>
                                            {delegationCheckLabel(clinicDelegationDocs)}
                                            <span style={styles.required}>{' (필수)'}</span>
                                        </span>
                                    </label>
                                    <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.6, marginTop: 4 }}>{delegationFootnote(clinicDelegationDocs.map(d => d.key))}</div>
                                </div>
                            )}

                            {/* 🏭 S5 — 「외주 수주 의무 확인(필수)」: 제조사 + 외주를 받겠다고 한 기공소. 라벨 법무 §1-4 글자 그대로(v8.0 시행 전/후 분기), 저장 consents.vendorPledges(판·문안 해시·약관 판). 처리방침 v3.3.5 시행 뒤에만 */}
                            {needPledge && (
                                <label style={styles.checkboxLabel}>
                                    <input type="checkbox" checked={agreedToPledge} onChange={(e) => setAgreedToPledge(e.target.checked)} style={styles.checkbox} />
                                    <span style={styles.checkboxText}>
                                        {vendorPledgeLabel(termsV80Live)}
                                        {termsV80Live && <span style={styles.required}>{' (필수)'}</span>}
                                    </span>
                                </label>
                            )}

                            {/* 마케팅 수신 (선택) */}
                            <label style={styles.checkboxLabel}>
                                <input type="checkbox" checked={agreedToMarketing} onChange={(e) => setAgreedToMarketing(e.target.checked)} style={styles.checkbox} />
                                <span style={styles.checkboxText}>
                                    이벤트·혜택·신규 서비스 안내 등 마케팅 정보 수신에 동의합니다
                                    <span style={{ ...styles.required, color: '#94a3b8' }}>(선택)</span>
                                </span>
                            </label>

                            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
                                ※ 마케팅 수신 동의는 거부하셔도 서비스 이용에 제한이 없습니다.
                                {'동의 철회는 고객센터(010-2972-3733)·전자우편(dentcluster@gmail.com)으로 요청할 수 있습니다.'}
                                {(userType === 'dental' || userType === 'lab') && (
                                    <><br/>{userType === 'dental'
                                        ? '※ 치과 회원은 무료로 이용합니다(2026-10-30).'
                                        : '※ 유료 구독 시작 시 별도의 결제 동의 절차가 진행됩니다 (기공소 월 55,000원부터, 부가세 포함·1개월 무료 체험).'}}</>
                                )}
                            </p>
                        </div>

                        {/* 제출 버튼 */}
                        <button
                            type="submit"
                            style={styles.submitButton}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div style={styles.spinner}></div>
                                    <span>처리 중...</span>
                                </>
                            ) : (
                                '가입하기'
                            )}
                        </button>
                    </form>
                )}

                {/* 로그인 링크 */}
                <div style={styles.footer}>
                    이미 계정이 있으신가요?{' '}
                    <button
                        onClick={onShowLogin}
                        style={styles.linkButton}
                        type="button"
                    >
                        로그인
                    </button>
                </div>
            </div>

            {/* Daum 우편번호 위젯 모달 */}
            {showAddressPicker && (
                <AddressPicker
                    onComplete={handleAddressComplete}
                    onClose={() => setShowAddressPicker(false)}
                />
            )}
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        padding: '20px',
    },
    box: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '40px',
        width: '100%',
        maxWidth: '500px',
    },
    header: {
        textAlign: 'center',
        marginBottom: '32px',
        position: 'relative',
    },
    backButton: {
        position: 'absolute',
        left: 0,
        top: 0,
        background: 'none',
        border: 'none',
        color: '#64748b',
        cursor: 'pointer',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
    },
    title: {
        margin: '16px 0 8px 0',
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
    },
    subtitle: {
        margin: 0,
        fontSize: '14px',
        color: '#64748b',
    },
    typeSelection: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '16px',
        marginBottom: '24px',
    },
    typeCard: {
        padding: '24px',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    typeTitle: {
        margin: '12px 0 8px 0',
        fontSize: '18px',
        fontWeight: '600',
        color: '#1e293b',
    },
    typeDesc: {
        margin: '0 0 12px 0',
        fontSize: '13px',
        color: '#64748b',
        lineHeight: '1.5',
    },
    typeBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: '#e0e7ff',
        color: '#3730a3',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    businessTypeLabel: {
        padding: '4px 12px',
        backgroundColor: '#e0e7ff',
        color: '#4338ca',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
    },
    input: {
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        transition: 'all 0.2s',
    },
    error: {
        padding: '12px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#dc2626',
        fontSize: '14px',
        textAlign: 'center',
    },
    agreementBox: {
        padding: '16px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        cursor: 'pointer',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        cursor: 'pointer',
        marginTop: '2px',
        flexShrink: 0,
    },
    checkboxText: {
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.6',
    },
    linkText: {
        background: 'none',
        border: 'none',
        color: '#6366f1',
        fontWeight: '600',
        textDecoration: 'underline',
        cursor: 'pointer',
        padding: 0,
        fontSize: '14px',
    },
    required: {
        color: '#dc2626',
        fontWeight: '600',
    },
    submitButton: {
        padding: '14px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
    },
    spinner: {
        width: '16px',
        height: '16px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: '2px solid #ffffff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    footer: {
        marginTop: '24px',
        textAlign: 'center',
        fontSize: '14px',
        color: '#64748b',
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#6366f1',
        cursor: 'pointer',
        fontWeight: '600',
        textDecoration: 'underline',
    },
    categoryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
    },
    categoryCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 14px',
        border: '2px solid #e2e8f0',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
};

export default Register;